import {
  Partner,
  PartnerWithContacts,
  PartnersResponse,
  PartnerResponse,
  PartnerStatsResponse,
  PartnerPagination,
  PartnerSummary,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  PartnerStats,
  PartnerStatus
} from '../types/partner'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class PartnerService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all partners with optional filtering and pagination
   * @param customerId - Customer ID (required)
   * @param search - Optional search query (searches partner name)
   * @param status - Optional filter by status
   * @param categoryId - Optional filter by category ID
   * @param tierId - Optional filter by tier ID
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @param startDate - Optional start date for metrics filtering (YYYY-MM-DD)
   * @param endDate - Optional end date for metrics filtering (YYYY-MM-DD)
   */
  async getPartners(
    customerId: string,
    search?: string,
    status?: PartnerStatus,
    categoryId?: string,
    tierId?: string,
    page?: number,
    limit?: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ data: Partner[]; summary: PartnerSummary; pagination: PartnerPagination }> {
    const url = new URL(`${this.baseURL}/partners`)

    // Required parameter
    url.searchParams.append('customerId', customerId)

    if (search !== undefined && search.trim()) {
      url.searchParams.append('search', search.trim())
    }
    if (status !== undefined) {
      url.searchParams.append('status', status)
    }
    if (categoryId !== undefined) {
      url.searchParams.append('category_id', categoryId)
    }
    if (tierId !== undefined) {
      url.searchParams.append('tier_id', tierId)
    }
    if (page !== undefined) {
      url.searchParams.append('page', String(page))
    }
    if (limit !== undefined) {
      url.searchParams.append('limit', String(limit))
    }
    if (startDate !== undefined) {
      url.searchParams.append('startDate', startDate)
    }
    if (endDate !== undefined) {
      url.searchParams.append('endDate', endDate)
    }

    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<PartnersResponse>(response)

    return {
      data: responseData.data,
      summary: responseData.summary,
      pagination: responseData.pagination
    }
  }

  /**
   * Get a single partner by ID
   * @param id - Partner UUID
   */
  async getPartner(id: string): Promise<PartnerWithContacts> {
    const url = `${this.baseURL}/partners/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerResponse>(response)

    return data.data as PartnerWithContacts
  }

  /**
   * Create a new partner
   * @param customerId - Customer ID (required)
   * @param partnerData - Partner data
   */
  async createPartner(customerId: string, partnerData: CreatePartnerRequest): Promise<Partner> {
    const url = new URL(`${this.baseURL}/partners`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(partnerData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<PartnerResponse>(response)

    return data.data as Partner
  }

  /**
   * Update an existing partner
   * @param id - Partner UUID
   * @param customerId - Customer ID (required)
   * @param updates - Partial partner data to update
   */
  async updatePartner(id: string, customerId: string, updates: UpdatePartnerRequest): Promise<Partner> {
    const url = new URL(`${this.baseURL}/partners/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<PartnerResponse>(response)

    return data.data as Partner
  }

  /**
   * Delete a partner (soft delete)
   * @param id - Partner UUID
   */
  async deletePartner(id: string): Promise<void> {
    const url = `${this.baseURL}/partners/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }

  /**
   * Get partner statistics (contact count)
   * @param id - Partner UUID
   */
  async getPartnerStats(id: string): Promise<PartnerStats> {
    const url = `${this.baseURL}/partners/${id}/stats`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerStatsResponse>(response)

    return data.data
  }

  /**
   * Get the proxy URL for a partner's logo
   * This routes through our backend to access Azure Blob Storage
   * @param partnerId - Partner UUID
   * @param customerId - Customer UUID
   * @param cacheBuster - Optional cache buster (e.g., updated_at timestamp) to force fresh request
   * @returns The proxy URL for the logo
   */
  getLogoProxyUrl(partnerId: string, customerId: string, cacheBuster?: string | number): string {
    let url = `${this.baseURL}/partners/${partnerId}/logo?customerId=${customerId}`
    if (cacheBuster) {
      url += `&v=${cacheBuster}`
    }
    return url
  }
}

export const partnerService = new PartnerService()

/**
 * Helper function to get the proxy URL for a partner logo
 * Use this instead of partner.logo_url for displaying logos
 * @param partnerId - Partner UUID
 * @param customerId - Customer UUID
 * @param cacheBuster - Optional cache buster (e.g., partner.updated_at) to force fresh request after logo changes
 */
export const getPartnerLogoUrl = (partnerId: string, customerId: string, cacheBuster?: string | number): string => {
  return partnerService.getLogoProxyUrl(partnerId, customerId, cacheBuster)
}
